import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { db } from '../../firebase/client';
import { formatDateReadable } from '../../utils/dateUtils';
import { AppContext } from '../../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  History, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  FileText, 
  PhoneCall,
  Search,
  Zap,
  CreditCard,
  ShieldCheck,
  ExternalLink,
  Calendar
} from 'lucide-react';

interface PaymentRecord {
    id: string;
    amount: number;
    plan: string;
    type?: 'subscription' | 'boost';
    category?: 'subscription_initial' | 'subscription_renewal' | 'featured_initial' | 'featured_renewal';
    status: 'pending' | 'validated' | 'rejected';
    timestamp: Timestamp;
    startDate?: Timestamp;
    endDate?: Timestamp;
    subscriptionEndDate?: Timestamp;
    proofUrl?: string;
    description?: string;
}

const PaymentHistoryScreen: React.FC = () => {
    const { currentUser } = useAuth();
    const { getRestaurantById, restaurants } = useData();
    const { setView } = React.useContext(AppContext);

    const restaurant = getRestaurantById(currentUser?.restaurantId || '') || 
        restaurants.find(r => r.ownerId === currentUser?.uid || r.owner_id === currentUser?.uid);

    const [payments, setPayments] = useState<PaymentRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedProofUrl, setSelectedProofUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchPayments = async () => {
            if (!restaurant) return;
            try {
                // Remove orderBy to avoid excluding documents missing the ordered field
                const q = query(
                    collection(db, 'payments'),
                    or(
                        where('restaurantId', '==', restaurant.id),
                        where('id_restaurant', '==', restaurant.id),
                        where('restaurant_id', '==', restaurant.id)
                    )
                );
                const snapshot = await getDocs(q);
                
                const fetchedPayments = snapshot.docs.map(doc => {
                    const data = doc.data();
                    
                        // Normalize fields for backward compatibility
                        const rawEndDate = data.endDate || data.subscriptionEndDate || data.expiryDate || data.validUntil;
                        
                        return {
                            id: doc.id,
                            amount: data.amount || 0,
                            // Handle 'plan' vs 'planType'
                            plan: data.plan || data.planType || 'mensuel',
                            // Handle 'boost' vs 'featured'
                            type: data.type === 'featured' ? 'boost' : (data.type || 'subscription'),
                            category: data.category,
                            // Handle 'approved' status as equivalent to 'validated'
                            status: data.status === 'approved' ? 'validated' : (data.status || 'pending'),
                            // Handle 'timestamp' vs 'createdAt'
                            timestamp: data.timestamp || data.createdAt || Timestamp.now(),
                            proofUrl: data.proofUrl,
                            startDate: data.startDate,
                            endDate: rawEndDate,
                            subscriptionEndDate: rawEndDate,
                            description: data.description || ''
                        } as PaymentRecord;
                });

                // Sort in memory (descending by timestamp)
                fetchedPayments.sort((a, b) => {
                    const timeA = a.timestamp?.seconds || 0;
                    const timeB = b.timestamp?.seconds || 0;
                    return timeB - timeA;
                });

                setPayments(fetchedPayments);
            } catch (error) {
                console.error("Error fetching payments:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPayments();
    }, [restaurant]);

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'validated':
                return { 
                    color: 'bg-green-100 text-green-700 border-green-200', 
                    icon: <CheckCircle2 size={14} />, 
                    label: 'Validé',
                    bg: 'bg-green-500/5'
                };
            case 'pending':
                return { 
                    color: 'bg-blue-100 text-blue-700 border-blue-200', 
                    icon: <Clock size={14} />, 
                    label: 'En attente',
                    bg: 'bg-blue-500/5'
                };
            case 'rejected':
                return { 
                    color: 'bg-red-100 text-red-700 border-red-200', 
                    icon: <XCircle size={14} />, 
                    label: 'Refusé',
                    bg: 'bg-red-500/5'
                };
            default:
                return { 
                    color: 'bg-gray-100 text-gray-700 border-gray-200', 
                    icon: <Clock size={14} />, 
                    label: 'Inconnu',
                    bg: 'bg-gray-100'
                };
        }
    };

    if (!restaurant) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full"
            />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Header */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-20 px-6 py-5 flex items-center gap-4">
                <button 
                    onClick={() => setView({ screen: 'adminAccount' })}
                    className="p-2.5 rounded-2xl bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-700 transition-all border border-gray-100"
                >
                    <ArrowLeft size={24} />
                </button>
                <div>
                    <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Historique des Paiements</h1>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Transactions & Validations</p>
                </div>
            </div>

            <div className="max-w-xl mx-auto p-6">
                <AnimatePresence mode="wait">
                    {loading ? (
                        <motion.div 
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center py-32"
                        >
                            <motion.div 
                                animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="text-red-600 mb-6"
                            >
                                <History size={48} />
                            </motion.div>
                            <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] animate-pulse">Synchronisation...</p>
                        </motion.div>
                    ) : payments.length === 0 ? (
                        <motion.div 
                            key="empty"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-[2.5rem] border border-dashed border-gray-200 p-12 text-center shadow-sm"
                        >
                            <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center text-gray-300 mx-auto mb-6">
                                <Search size={40} />
                            </div>
                            <h3 className="text-lg font-black text-gray-900">Aucun résultat</h3>
                            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                                Vous n'avez pas encore effectué de transactions sur la plateforme.
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div 
                            key="list"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="space-y-4"
                        >
                            {payments.map((payment, index) => {
                                 const config = getStatusConfig(payment.status);
                                 const isBoost = payment.type === 'boost';
 
                                 return (
                                     <motion.div 
                                         key={payment.id}
                                         initial={{ opacity: 0, x: -20 }}
                                         animate={{ opacity: 1, x: 0 }}
                                         transition={{ 
                                             delay: index * 0.08,
                                             type: "spring",
                                             stiffness: 100,
                                             damping: 15
                                         }}
                                         whileHover={{ y: -4, transition: { duration: 0.2 } }}
                                         className={`group relative bg-white rounded-[2rem] border border-gray-100 p-6 flex flex-col gap-4 shadow-sm hover:shadow-2xl hover:shadow-gray-200/50 hover:border-red-200 transition-all ${config.bg}`}
                                     >
                                         <div className="flex justify-between items-start">
                                             <div className="flex items-center gap-4">
                                                 <motion.div 
                                                     whileHover={{ scale: 1.1, rotate: 5 }}
                                                     className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${isBoost ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white' : 'bg-gradient-to-br from-red-500 to-red-700 text-white'}`}
                                                 >
                                                     {isBoost ? <Zap size={28} /> : <ShieldCheck size={28} />}
                                                 </motion.div>
                                                 <div>
                                                     <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                                         <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${isBoost ? 'bg-indigo-100 text-indigo-700' : 'bg-red-100 text-red-700'}`}>
                                                              {isBoost ? 'En Vedette' : (
                                                                 payment.plan === 'mensuel' ? 'Mensuel' :
                                                                 payment.plan === 'annuel' ? 'Annuel' :
                                                                 payment.plan === 'commission' ? 'Commission' :
                                                                 payment.plan.startsWith('featured_') ? 'En Vedette' :
                                                                 payment.plan.charAt(0).toUpperCase() + payment.plan.slice(1)
                                                              )}
                                                         </span>
                                                          {isBoost && (
                                                              <span className="text-[9px] font-black bg-purple-600 text-white px-2 py-0.5 rounded-full uppercase tracking-tighter shadow-sm shadow-purple-200">
                                                                  {payment.plan === 'featured_1_week' ? 'Star' : 
                                                                   payment.plan === 'featured_2_weeks' ? 'Pro' : 
                                                                   payment.plan === 'featured_1_month' ? 'Ultra' : 
                                                                   payment.plan === '1_week' ? 'Star' :
                                                                   payment.plan === '15_days' ? 'Pro' :
                                                                   payment.plan === '1_month' ? 'Ultra' : 'En Vedette'}
                                                              </span>
                                                          )}
                                                         {payment.category?.includes('renewal') && (
                                                             <span className="text-[9px] font-black bg-blue-600 text-white px-2 py-0.5 rounded-full uppercase tracking-tighter shadow-sm shadow-blue-200">
                                                                 RENOUVELLEMENT
                                                             </span>
                                                         )}
                                                     </div>
                                                     <h3 className="text-2xl font-black text-gray-900 tracking-tight leading-none mb-1">
                                                         {payment.amount.toLocaleString()} <span className="text-sm font-bold text-gray-400">DA</span>
                                                     </h3>
                                                      {payment.status === "validated" && (() => {
                                                          // Try payment's own endDate first, then fallback to restaurant.subscriptionExpiry
                                                          const endSecs = payment.endDate?.seconds || payment.subscriptionEndDate?.seconds;
                                                          const fallbackExpiry = restaurant?.subscriptionExpiry as any;
                                                          const fallbackSecs = fallbackExpiry?.seconds || (fallbackExpiry ? Math.floor(new Date(fallbackExpiry).getTime() / 1000) : null);
                                                          const displaySecs = endSecs || (index === 0 ? fallbackSecs : null); // fallback only on latest payment
                                                          if (!displaySecs) return null;
                                                          return (
                                                              <div className="flex items-center gap-1.5 opacity-80 mt-1">
                                                                  <Calendar size={11} className="text-green-600" />
                                                                  <span className="text-[10px] font-black text-green-700 uppercase tracking-tight">
                                                                      Valide jusqu'au {formatDateReadable(displaySecs * 1000)}
                                                                  </span>
                                                              </div>
                                                          );
                                                      })()}
                                                   </div>
                                               </div> 
                                             <div className="flex flex-col items-end gap-2">
                                                 <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black border uppercase tracking-wider shadow-sm ${config.color}`}>
                                                     {config.icon}
                                                     {config.label}
                                                 </div>
                                                 <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest">
                                                     {formatDateReadable(payment.timestamp)}
                                                 </p>
                                             </div>
                                         </div>
 
                                         {payment.status === 'validated' && (payment as any).startDate && (
                                             <motion.div 
                                                 initial={{ opacity: 0, height: 0 }}
                                                 animate={{ opacity: 1, height: 'auto' }}
                                                 className={`${isBoost ? 'bg-indigo-50/70 border-indigo-200/50 text-indigo-900' : 'bg-red-50/70 border-red-200/50 text-red-900'} p-4 rounded-2xl border flex items-center justify-between shadow-inner mt-2`}
                                             >
                                                 <div className="flex items-center gap-3">
                                                     <div className={`p-2 bg-white rounded-lg shadow-sm ${isBoost ? 'text-indigo-600' : 'text-red-600'}`}>
                                                         <Calendar size={16} />
                                                     </div>
                                                     <span className="text-[10px] font-black uppercase tracking-widest">Validité {isBoost ? 'En Vedette' : 'Abonnement'}</span>
                                                 </div>
                                                 <div className={`text-xs font-black ${isBoost ? 'text-indigo-700' : 'text-red-700'} bg-white px-3 py-1.5 rounded-xl shadow-sm border ${isBoost ? 'border-indigo-100' : 'border-red-100'}`}>
                                                     {formatDateReadable(payment.startDate)} — {formatDateReadable(payment.endDate)}
                                                 </div>
                                             </motion.div>
                                         )}
 
                                         <div className="flex items-center justify-between pt-4 border-t border-gray-100/50">
                                             <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-lg">
                                                 <FileText size={14} className="opacity-50" />
                                                 REF: {payment.id.slice(0, 8).toUpperCase()}
                                             </div>
 
                                             {payment.proofUrl && (
                                                 <motion.button 
                                                     whileHover={{ scale: 1.05, x: -5 }}
                                                     whileTap={{ scale: 0.95 }}
                                                     onClick={() => setSelectedProofUrl(payment.proofUrl)}
                                                     className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-gray-200 group-hover:bg-red-600 group-hover:shadow-red-100"
                                                 >
                                                     <ExternalLink size={12} />
                                                     VOIR LE REÇU
                                                 </motion.button>
                                             )}
                                         </div>
 
                                         {/* Stylized background text */}
                                         <div className="absolute -right-4 -bottom-4 opacity-[0.03] select-none pointer-events-none group-hover:opacity-[0.05] transition-opacity">
                                             <span className="text-8xl font-black text-gray-900 uppercase tracking-tighter">
                                                 {payment.status === 'validated' ? 'PAID' : payment.status.toUpperCase()}
                                             </span>
                                         </div>
                                     </motion.div>
                                 );
                             })}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Support Card */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-16 bg-gradient-to-br from-red-600 to-rose-700 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl shadow-red-200"
                >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -translate-y-12 translate-x-12" />
                    <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md">
                                <PhoneCall size={24} />
                            </div>
                            <h4 className="text-xl font-black tracking-tight">Besoin d'assistance ?</h4>
                        </div>
                        <p className="text-sm font-medium text-red-50 leading-relaxed max-w-xs">
                            Si vous rencontrez un problème avec une transaction ou si votre validation tarde, contactez-nous directement.
                        </p>
                        <div className="mt-6 flex items-center justify-between">
                            <span className="text-lg font-black tracking-widest">0661269692</span>
                            <div className="bg-white/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider backdrop-blur-md">
                                Support 24/7
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Proof Modal */}
            <AnimatePresence>
                {selectedProofUrl && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
                        onClick={() => setSelectedProofUrl(null)}
                    >
                        <motion.button
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-2xl flex items-center justify-center backdrop-blur-md transition-all z-[110]"
                            onClick={() => setSelectedProofUrl(null)}
                        >
                            <XCircle size={32} />
                        </motion.button>

                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative max-w-4xl w-full max-h-[80vh] flex items-center justify-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img 
                                src={selectedProofUrl} 
                                alt="Preuve de paiement" 
                                className="max-w-full max-h-[80vh] object-contain rounded-3xl shadow-2xl border-4 border-white/10"
                            />
                            
                            <div className="absolute -bottom-12 left-0 right-0 flex justify-center">
                                <span className="bg-white/10 text-white/60 text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full backdrop-blur-md">
                                    Aperçu Reçu Yumii
                                </span>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PaymentHistoryScreen;
